import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Clock, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn, formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Pagination from '@/components/ui/Pagination';
import DocumentHead from '@/components/DocumentHead';

const LEVEL_BADGE_VARIANT: Record<string, 'green' | 'yellow' | 'blue' | 'red'> = {
  beginner: 'green',
  intermediate: 'yellow',
  advanced: 'red',
};

function useCourses(params: { page?: number; limit?: number; level?: string }) {
  const { page = 1, limit = 12, level } = params;
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('limit', String(limit));
  if (level) qs.set('level', level);

  return useQuery({
    queryKey: ['courses', params],
    queryFn: () =>
      api.get<{ courses: any[]; total: number; page: number; limit: number }>(
        `/courses?${qs}`,
      ),
  });
}

export default function CoursesCatalog() {
  const { t } = useTranslation('courses');
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const level = searchParams.get('level') || '';

  const { data, isLoading } = useCourses({ page, limit: 12, level: level || undefined });

  const courses = data?.courses ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 12);

  const levels = useMemo(
    () => [
      { label: t('catalog.filters.allLevels'), value: '' },
      { label: t('catalog.filters.beginner'), value: 'beginner' },
      { label: t('catalog.filters.intermediate'), value: 'intermediate' },
      { label: t('catalog.filters.advanced'), value: 'advanced' },
    ],
    [t],
  );

  const levelLabel = useCallback(
    (key: string) => {
      const localized = t(`detail.levels.${key}`, { defaultValue: '' });
      if (localized) return localized;
      return key.charAt(0).toUpperCase() + key.slice(1);
    },
    [t],
  );

  const setPage = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(newPage));
      setSearchParams(params);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [searchParams, setSearchParams],
  );

  function handleLevelChange(newLevel: string) {
    const params = new URLSearchParams(searchParams);
    if (newLevel) {
      params.set('level', newLevel);
    } else {
      params.delete('level');
    }
    params.delete('page');
    setSearchParams(params);
  }

  return (
    <div className="bg-[--bg-warm] min-h-screen">
      <DocumentHead
        title={t('catalog.documentHead.title')}
        description={t('catalog.documentHead.description')}
        canonicalUrl="/courses"
      />

      {/* Hero */}
      <div className="bg-cta text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl font-bold">{t('catalog.hero.heading')}</h1>
          <p className="mt-3 text-lg text-white/80 max-w-2xl mx-auto">
            {t('catalog.hero.subheading')}
          </p>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav aria-label={t('catalog.breadcrumbs.label')} className="mb-6">
          <ol className="flex items-center gap-1.5 text-sm text-ink-muted">
            <li>
              <Link to="/" className="hover:text-ink-soft transition-colors">
                {t('catalog.breadcrumbs.home')}
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="h-4 w-4" />
            </li>
            <li>
              <span className="font-medium text-ink" aria-current="page">
                {t('catalog.breadcrumbs.courses')}
              </span>
            </li>
          </ol>
        </nav>

        {/* Level filter tabs */}
        <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label={t('catalog.filters.label')}>
          {levels.map((l) => (
            <button
              key={l.value}
              role="tab"
              aria-selected={level === l.value}
              onClick={() => handleLevelChange(l.value)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-600)]',
                level === l.value
                  ? 'bg-cta text-white'
                  : 'bg-surface text-ink-soft border border-line hover:bg-surface-soft',
              )}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Results count */}
        {!isLoading && (
          <p className="mb-6 text-sm text-ink-muted">
            {t('catalog.resultsCount', { count: total })}
          </p>
        )}

        {/* Content */}
        {isLoading ? (
          <div
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
            aria-busy="true"
            aria-label={t('catalog.loadingAriaLabel')}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-xl border border-line bg-surface overflow-hidden" aria-hidden="true">
                <div className="aspect-video bg-surface-muted" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-surface-muted rounded w-1/4" />
                  <div className="h-5 bg-surface-muted rounded w-3/4" />
                  <div className="h-4 bg-surface-muted rounded w-full" />
                  <div className="h-4 bg-surface-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : courses.length === 0 ? (
          <div className="py-16 text-center">
            <BookOpen className="mx-auto h-12 w-12 text-ink-muted" aria-hidden="true" />
            <h2 className="mt-4 text-lg font-semibold text-ink">{t('catalog.empty.heading')}</h2>
            <p className="mt-2 text-ink-soft">
              {level ? t('catalog.empty.filtered') : t('catalog.empty.default')}
            </p>
            {level && (
              <Button
                variant="boutique-outline"
                className="mt-4"
                onClick={() => handleLevelChange('')}
              >
                {t('catalog.empty.viewAll')}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {courses.map((course: any) => {
                const hasFoundersPricing =
                  course.founders_pricing_id &&
                  course.slots_used < course.max_slots;
                const displayPrice = hasFoundersPricing
                  ? Number(course.founders_price)
                  : Number(course.price);
                const originalPrice = Number(course.price);
                const compareAtPrice = course.compare_at_price
                  ? Number(course.compare_at_price)
                  : null;
                const durationHours = course.total_duration != null ? Math.round(course.total_duration / 60) : 0;
                const durationMinutes = course.total_duration != null ? course.total_duration % 60 : 0;

                return (
                  <Link
                    key={course.id}
                    to={`/courses/${course.slug}`}
                    className="group rounded-xl border border-line bg-surface shadow-sm overflow-hidden transition-shadow hover:shadow-md flex flex-col"
                  >
                    {/* Thumbnail */}
                    <div className="aspect-video bg-[var(--color-primary-50)] flex items-center justify-center overflow-hidden">
                      {course.thumbnail_url ? (
                        <img
                          src={course.thumbnail_url}
                          alt={course.name}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <BookOpen className="h-12 w-12 text-[var(--color-primary-300)]" aria-hidden="true" />
                      )}
                    </div>

                    <div className="p-5 flex flex-col flex-1">
                      {/* Level badge */}
                      <div className="flex items-center gap-2 mb-2">
                        {course.level && (
                          <Badge variant={LEVEL_BADGE_VARIANT[course.level] || 'gray'}>
                            {levelLabel(course.level)}
                          </Badge>
                        )}
                        {hasFoundersPricing && (
                          <Badge variant="blue">{t('catalog.card.foundersBadge')}</Badge>
                        )}
                      </div>

                      {/* Name */}
                      <h2 className="text-lg font-bold text-ink group-hover:text-[var(--color-primary-700)] transition-colors">
                        {course.name}
                      </h2>

                      {/* Description */}
                      {course.short_description && (
                        <p className="mt-1 text-sm text-ink-muted line-clamp-2">
                          {course.short_description}
                        </p>
                      )}

                      <div className="mt-auto pt-4">
                        {/* Price */}
                        <div className="flex items-baseline gap-2">
                          {course.price_type === 'free' ? (
                            <span className="text-lg font-bold text-sage-700">{t('catalog.card.free')}</span>
                          ) : (
                            <>
                              <span className="text-xl font-bold text-[var(--color-primary-700)]">
                                {formatCurrency(displayPrice)}
                              </span>
                              {hasFoundersPricing && displayPrice < originalPrice && (
                                <span className="text-sm text-ink-muted line-through">
                                  {formatCurrency(originalPrice)}
                                </span>
                              )}
                              {!hasFoundersPricing && compareAtPrice && compareAtPrice > originalPrice && (
                                <span className="text-sm text-ink-muted line-through">
                                  {formatCurrency(compareAtPrice)}
                                </span>
                              )}
                            </>
                          )}
                        </div>

                        {/* Meta */}
                        <div className="mt-3 flex items-center gap-4 text-xs text-ink-muted">
                          {course.lesson_count != null && (
                            <span className="flex items-center gap-1">
                              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                              {t('catalog.card.lessonsCount', { count: course.lesson_count })}
                            </span>
                          )}
                          {course.total_duration != null && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                              {t('catalog.card.durationHoursMinutes', { hours: durationHours, minutes: durationMinutes })}
                            </span>
                          )}
                        </div>

                        {/* CTA */}
                        <div className="mt-4">
                          <span className="inline-flex items-center justify-center w-full h-10 px-4 text-sm font-medium rounded-lg bg-cta text-white group-hover:bg-cta-deep transition-colors">
                            {t('catalog.card.viewCourse')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="mt-8 flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
